/*
 * Modal de drill-down multinivel (funnel): agrupa por claves encadenadas
 * hasta llegar al detalle de DOs. Réplica del engine de modales del HTML original.
 */
import { useEffect, useState } from "react";
import { Button, Description, Modal, ModalBody, ModalHeader, Title } from "codekit";

import Simtable, { Valspanx } from "./Simtable";
import { fmtCOP, htmlTheme, keyLabels } from "./utils/carthelp";

/**
 * Agrupa un conjunto de filas por una clave sumando conteo y valor
 * @param {Array} mScope Filas del nivel actual
 * @param {string} sKey Clave de agrupacion
 * @returns {Array} Entradas [label, { count, valor }] ordenadas por valor desc
 */
const groupEntries = (mScope, sKey) => {
  const mGrouped = {};
  mScope.forEach((mRow) => {
    const sK = mRow[sKey] ?? "N/A";
    if (!mGrouped[sK]) mGrouped[sK] = { count: 0, valor: 0 };
    mGrouped[sK].count++;
    mGrouped[sK].valor += mRow.VALOR;
  });
  return Object.entries(mGrouped).sort((a, b) => b[1].valor - a[1].valor);
};

//Columnas del detalle final de DOs
const detailColumns = [
  { key: "DO", title: "DO" },
  { key: "NOMBRE", title: "Cliente" },
  { key: "TIPO DE OPERACIÓN", title: "Tipo" },
  { key: "_fechaCreacionStr", title: "Fecha Creación" },
  { key: "_fechaLevanteStr", title: "Fecha Levante" },
  { key: "_dias", title: "Días", render: (r) => r._dias ?? "-" },
  { key: "VALOR", title: "Valor", render: (r) => <Valspanx value={r.VALOR} text={fmtCOP(r.VALOR)} /> },
];

/**
 * Modal de drill-down que navega niveles encadenados de agrupación
 * @param {Object} props Propiedades del componente
 * @param {Object} props.initial Nivel inicial { scope, keys, breadcrumb }
 * @param {Function} props.onClose Cierra el modal
 */
const Funnelmd = ({ initial, onClose }) => {
  const [history, setHistory] = useState([initial]);

  useEffect(() => {
    setHistory([initial]);
  }, [initial]);

  const mState = history[history.length - 1];
  const { scope, keys, breadcrumb } = mState;

  //Baja un nivel filtrando por la etiqueta seleccionada
  const drillInto = (sLabel) => {
    const sKey = keys[0];
    const mNewScope = scope.filter((mRow) => (mRow[sKey] ?? "N/A") === sLabel);
    setHistory((mPrev) => [
      ...mPrev,
      { scope: mNewScope, keys: keys.slice(1), breadcrumb: [...breadcrumb, sLabel] },
    ]);
  };

  //Regresa un nivel en la navegación
  const goBack = () => setHistory((mPrev) => (mPrev.length > 1 ? mPrev.slice(0, -1) : mPrev));

  const bIsDetail = keys.length === 0;
  const sTitle = bIsDetail ? `Detalle de DOs (${scope.length})` : keyLabels[keys[0]] || keys[0];

  const groupColumns = [
    { key: "label", title: keyLabels[keys[0]] || keys[0] || "" },
    { key: "count", title: "# DOs" },
    {
      key: "valor",
      title: "Valor",
      render: (r) => <span style={{ color: htmlTheme.navy2, fontWeight: 700 }}>{fmtCOP(r.valor)}</span>,
    },
  ];
  const mGroupRows = bIsDetail ? [] : groupEntries(scope, keys[0]).map(([label, v]) => ({ label, ...v }));
  const mDetailRows = bIsDetail ? [...scope].sort((a, b) => b.VALOR - a.VALOR) : [];

  return (
    <Modal isOpen size="xxl" onClose={onClose}>
      <ModalHeader>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ color: htmlTheme.muted }}>
              <Description variant="description-3">{breadcrumb.join(" › ")}</Description>
            </div>
            <div style={{ color: htmlTheme.navy2, marginTop: 2 }}>
              <Title variant="title-2">{sTitle}</Title>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {history.length > 1 ? (
              <Button variant="secondary" size="m" onClick={goBack}>
                ‹ Atrás
              </Button>
            ) : null}
            <Button variant="secondary" icon="close" onlyIcon size="m" onClick={onClose} />
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        <div style={{ padding: "14px 20px 20px" }}>
          {bIsDetail ? (
            <Simtable columns={detailColumns} rows={mDetailRows} maxHeight={520} />
          ) : (
            <Simtable columns={groupColumns} rows={mGroupRows} onRowClick={(mRow) => drillInto(mRow.label)} maxHeight={520} />
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default Funnelmd;
